import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { AuthService } from '../services/auth.service';

/**
 * Factory for a guard that allows access only if the current user has at
 * least one of the listed roles.
 *
 *   { path: 'admin', canActivate: [roleGuard('ADMIN')] }
 *   { path: 'staff', canActivate: [roleGuard('ADMIN', 'SUPPORT')] }
 *
 * Awaits `auth.whenReady()` first so the initial silent refresh on page
 * reload has time to settle before we judge — otherwise we'd flash /signin
 * on every reload even with a valid refresh token.
 */
export const roleGuard =
  (...roles: string[]): CanActivateFn =>
  async (_route, state) => {
    const auth = inject(AuthService);
    const router = inject(Router);

    await auth.whenReady();

    if (!auth.isAuthenticated()) {
      return router.createUrlTree(['/signin'], {
        queryParams: { returnUrl: state.url },
      });
    }
    if (auth.hasAnyRole(...roles)) {
      return true;
    }
    return router.createUrlTree(['/forbidden']);
  };
